import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ShieldCheck, Loader2, CheckCircle2, XCircle, Save, Sparkles, Users, FileSignature, ExternalLink, Image, Mail } from "lucide-react";
import { toast } from "sonner";

const SOURCE_LABEL = { db: "Database (cifrata)", env: "Variabile d'ambiente", none: "Non configurata" };

function StatusBadge({ configured }) {
  return (
    <span data-testid="status-badge"
      className={`text-xs font-semibold px-3 py-1 rounded-full ${
        configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
      }`}>
      {configured ? "Configurata" : "Non configurata"}
    </span>
  );
}

function Field({ meta, field, value, onChange }) {
  const Comp = meta.multiline ? Textarea : Input;
  return (
    <div>
      <Label htmlFor={meta.name} className="text-sm">{meta.label}</Label>
      <Comp id={meta.name} data-testid={`field-${meta.name}`}
        type={meta.secret && !meta.multiline ? "password" : "text"}
        value={value}
        onChange={(e) => onChange(meta.name, e.target.value)}
        placeholder={meta.secret && field?.configured ? `Attuale: ${field.masked} — lascia vuoto per non cambiare` : meta.placeholder}
        className={`mt-1.5 ${meta.multiline ? "font-mono text-xs h-28" : "font-mono"}`} />
      {field && <p className="text-[11px] text-slate-400 mt-1">Origine: {SOURCE_LABEL[field.source] || "—"}</p>}
    </div>
  );
}

export default function Setup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState("");
  const [geminiTest, setGeminiTest] = useState(null);
  const [custTest, setCustTest] = useState(null);
  const [imgTest, setImgTest] = useState(null);
  const [m365Test, setM365Test] = useState(null);
  const [dsStatus, setDsStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/admin/settings/integrations")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Impossibile caricare le impostazioni"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setEdit = (name, val) => setEdits((e) => ({ ...e, [name]: val }));
  const fieldValue = (meta) =>
    edits[meta.name] !== undefined ? edits[meta.name]
      : (meta.secret ? "" : (data?.[meta.name]?.value || ""));

  const saveCard = async (cardId, fields) => {
    const payload = {};
    fields.forEach((m) => { if (edits[m.name] !== undefined) payload[m.name] = edits[m.name]; });
    if (Object.keys(payload).length === 0) { toast.info("Nessuna modifica da salvare"); return; }
    setSaving(cardId);
    try {
      const { data: res } = await api.put("/admin/settings/integrations", payload);
      setData(res);
      setEdits((e) => { const c = { ...e }; fields.forEach((m) => delete c[m.name]); return c; });
      toast.success("Salvato e cifrato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setSaving("");
    }
  };

  const runGeminiTest = async () => {
    setBusy("gemini"); setGeminiTest(null);
    try { const { data: r } = await api.post("/admin/settings/integrations/test"); setGeminiTest(r); }
    catch (e) { setGeminiTest({ ok: false, detail: formatApiError(e.response?.data?.detail) || "Errore" }); }
    finally { setBusy(""); }
  };
  const runCustTest = async () => {
    setBusy("cust"); setCustTest(null);
    try { const { data: r } = await api.post("/admin/settings/integrations/test-customers"); setCustTest(r); }
    catch (e) { setCustTest({ ok: false, detail: formatApiError(e.response?.data?.detail) || "Errore" }); }
    finally { setBusy(""); }
  };
  const runImgTest = async () => {
    setBusy("img"); setImgTest(null);
    try { const { data: r } = await api.post("/admin/settings/integrations/test-images"); setImgTest(r); }
    catch (e) { setImgTest({ ok: false, detail: formatApiError(e.response?.data?.detail) || "Errore" }); }
    finally { setBusy(""); }
  };
  const runDsTest = async () => {
    setBusy("ds"); setDsStatus(null);
    try { const { data: r } = await api.get("/docusign/status"); setDsStatus(r); }
    catch (e) { setDsStatus({ connected: false, error: formatApiError(e.response?.data?.detail) || "Errore" }); }
    finally { setBusy(""); }
  };
  const runM365Test = async () => {
    setBusy("m365"); setM365Test(null);
    try { const { data: r } = await api.post("/admin/settings/integrations/test-m365"); setM365Test(r); }
    catch (e) { setM365Test({ ok: false, detail: formatApiError(e.response?.data?.detail) || "Errore" }); }
    finally { setBusy(""); }
  };

  const geminiMeta = [{ name: "gemini_api_key", label: "Chiave API Gemini", secret: true, placeholder: "Incolla la chiave (AIza… / AQ.…)" }];
  const custMeta = [{ name: "customers_api_url", label: "URL API Anagrafica", secret: true, placeholder: "https://portal.86bit.it/api/v1/..." }];
  const imgMeta = [
    { name: "google_search_api_key", label: "API Key Google", secret: true, placeholder: "AIza…" },
    { name: "google_search_cx", label: "Search Engine ID (cx)", placeholder: "es. a1b2c3d4e5f6g7h8i" },
  ];
  const m365Meta = [
    { name: "m365_tenant_id", label: "Tenant ID", placeholder: "es. BE9AD2FC-6857-4CCA-AC20-..." },
    { name: "m365_client_id", label: "Client ID", placeholder: "es. 1cef1d23-8ae2-4652-..." },
    { name: "m365_client_secret", label: "Client Secret", secret: true, placeholder: "Incolla il secret" },
    { name: "m365_sender", label: "Email mittente (mailbox)", placeholder: "es. info@86bit.it" },
    { name: "m365_cc", label: "CC automatico (opzionale)", placeholder: "es. commerciale@86bit.it (separa con virgola)" },
    { name: "m365_api_url", label: "API URL", placeholder: "https://graph.microsoft.com/v1.0" },
    { name: "m365_scope", label: "Scope", placeholder: "https://graph.microsoft.com/.default" },
    { name: "m365_redirect_uri", label: "Redirect URI", placeholder: "https://portal.86bit.it/customer/microsoft_tenant_res/callback" },
  ];
  const dsMeta = [
    { name: "docusign_integration_key", label: "Integration Key (Client ID)" },
    { name: "docusign_account_id", label: "Account ID" },
    { name: "docusign_user_id", label: "User ID (Impersonated)" },
    { name: "docusign_auth_server", label: "Auth Server", placeholder: "account.docusign.com" },
    { name: "docusign_base_path", label: "Base Path", placeholder: "https://eu.docusign.net/restapi" },
    { name: "docusign_redirect_uri", label: "Redirect URI" },
    { name: "docusign_rsa_private_key", label: "RSA Private Key", secret: true, multiline: true, placeholder: "-----BEGIN RSA PRIVATE KEY-----" },
  ];

  const TestResult = ({ res }) => res && (
    <div data-testid="test-result"
      className={`flex items-start gap-2 text-sm rounded-lg px-4 py-3 ${res.ok || res.connected ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>
      {(res.ok || res.connected) ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
      <span>
        {res.connected ? "DocuSign connesso correttamente." : (res.detail || res.error || "")}
        {res.sample ? ` (risposta: ${res.sample})` : ""}
        {res.consent_url && (
          <a href={res.consent_url} target="_blank" rel="noreferrer" className="ml-1 underline font-semibold inline-flex items-center gap-1">
            Concedi consenso <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </span>
    </div>
  );

  const Card = ({ id, icon: Icon, title, subtitle, configured, children }) => (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`setup-card-${id}`}>
      <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Icon className="w-5 h-5 text-blue-600" /></div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
        {!loading && configured !== undefined && <StatusBadge configured={configured} />}
      </div>
      <div className="p-6 space-y-5">{children}</div>
    </div>
  );

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Setup</h1>
        <p className="text-slate-500 mt-1">Configurazione delle integrazioni e delle chiavi API (cifrate AES-256).</p>
      </div>

      {loading ? <p className="text-slate-500">Caricamento…</p> : (
        <div className="max-w-2xl space-y-6">
          {/* Gemini */}
          <Card id="gemini" icon={Sparkles} title="Google Gemini AI"
            subtitle="Chiave per descrizioni e immagini generate dall'AI."
            configured={data?.gemini_api_key?.configured}>
            {geminiMeta.map((m) => <Field key={m.name} meta={m} field={data?.[m.name]} value={fieldValue(m)} onChange={setEdit} />)}
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => saveCard("gemini", geminiMeta)} disabled={saving === "gemini"} data-testid="save-gemini" className="bg-blue-600 hover:bg-blue-700">
                {saving === "gemini" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva
              </Button>
              <Button onClick={runGeminiTest} disabled={busy === "gemini" || !data?.gemini_api_key?.configured} variant="outline" data-testid="test-gemini">
                {busy === "gemini" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Verifica connessione
              </Button>
            </div>
            <TestResult res={geminiTest} />
          </Card>

          {/* Ricerca immagini web (Google) */}
          <Card id="images" icon={Image} title="Ricerca immagini web (Google)"
            subtitle="Foto reali dei prodotti dal web; l'AI viene usata solo se non si trova nulla."
            configured={data?.google_search_api_key?.configured}>
            <div className="grid sm:grid-cols-2 gap-4">
              {imgMeta.map((m) => <Field key={m.name} meta={m} field={data?.[m.name]} value={fieldValue(m)} onChange={setEdit} />)}
            </div>
            <p className="text-[11px] text-slate-400 -mt-2">
              Crea una API Key su Google Cloud (abilita "Custom Search API") e un motore su
              {" "}programmablesearchengine.google.com con "Cerca in tutto il web" e "Ricerca immagini" attivi (l'ID cx è nella Panoramica).
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => saveCard("images", imgMeta)} disabled={saving === "images"} data-testid="save-images" className="bg-blue-600 hover:bg-blue-700">
                {saving === "images" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva
              </Button>
              <Button onClick={runImgTest} disabled={busy === "img" || !data?.google_search_api_key?.configured} variant="outline" data-testid="test-images">
                {busy === "img" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />} Verifica connessione
              </Button>
            </div>
            <TestResult res={imgTest} />
          </Card>

          {/* Microsoft 365 (invio email) */}
          <Card id="m365" icon={Mail} title="Microsoft 365 (invio email)"
            subtitle="Invia le email direttamente dal tuo tenant M365 via Microsoft Graph (client credentials)."
            configured={data?.m365_client_secret?.configured}>
            <div className="grid sm:grid-cols-2 gap-4">
              {m365Meta.map((m) => <Field key={m.name} meta={m} field={data?.[m.name]} value={fieldValue(m)} onChange={setEdit} />)}
            </div>
            <p className="text-[11px] text-slate-400 -mt-2">
              Registra un'app su Microsoft Entra con permesso applicativo <strong>Mail.Send</strong> (consenso amministratore) e crea un Client Secret.
              L'email mittente deve essere una casella del tenant.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => saveCard("m365", m365Meta)} disabled={saving === "m365"} data-testid="save-m365" className="bg-blue-600 hover:bg-blue-700">
                {saving === "m365" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva
              </Button>
              <Button onClick={runM365Test} disabled={busy === "m365" || !data?.m365_client_secret?.configured} variant="outline" data-testid="test-m365">
                {busy === "m365" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Verifica connessione
              </Button>
            </div>
            <TestResult res={m365Test} />
          </Card>

          {/* Anagrafica */}
          <Card id="customers" icon={Users} title="Anagrafica clienti"
            subtitle="Endpoint API per importare i clienti."
            configured={data?.customers_api_url?.configured}>
            {custMeta.map((m) => <Field key={m.name} meta={m} field={data?.[m.name]} value={fieldValue(m)} onChange={setEdit} />)}
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => saveCard("customers", custMeta)} disabled={saving === "customers"} data-testid="save-customers" className="bg-blue-600 hover:bg-blue-700">
                {saving === "customers" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva
              </Button>
              <Button onClick={runCustTest} disabled={busy === "cust" || !data?.customers_api_url?.configured} variant="outline" data-testid="test-customers">
                {busy === "cust" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />} Verifica connessione
              </Button>
            </div>
            <TestResult res={custTest} />
          </Card>

          {/* DocuSign */}
          <Card id="docusign" icon={FileSignature} title="DocuSign (firma elettronica)"
            subtitle="Credenziali JWT per l'invio dei preventivi alla firma."
            configured={data?.docusign_integration_key?.configured}>
            <div className="grid sm:grid-cols-2 gap-4">
              {dsMeta.filter((m) => !m.multiline).map((m) => (
                <Field key={m.name} meta={m} field={data?.[m.name]} value={fieldValue(m)} onChange={setEdit} />
              ))}
            </div>
            {dsMeta.filter((m) => m.multiline).map((m) => (
              <Field key={m.name} meta={m} field={data?.[m.name]} value={fieldValue(m)} onChange={setEdit} />
            ))}
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => saveCard("docusign", dsMeta)} disabled={saving === "docusign"} data-testid="save-docusign" className="bg-blue-600 hover:bg-blue-700">
                {saving === "docusign" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva
              </Button>
              <Button onClick={runDsTest} disabled={busy === "ds"} variant="outline" data-testid="test-docusign">
                {busy === "ds" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSignature className="w-4 h-4 mr-2" />} Verifica connessione
              </Button>
            </div>
            <TestResult res={dsStatus} />
          </Card>

          <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-4 py-3 border border-slate-100 max-w-2xl">
            <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
            <span>
              Tutte le credenziali sono salvate <b>cifrate con AES-256-GCM</b> (crittografia di livello militare) nel database
              e usate automaticamente anche in <b>produzione</b>, senza dover modificare le variabili d'ambiente del deploy.
            </span>
          </div>
        </div>
      )}
    </Layout>
  );
}
